import { Container } from '../../../odie';
import { GoalId } from '../../../game/defs/goal';
import { BasicHandler } from '../../../lib/defs/types';
import { numberClamp } from '../../../lib/util/mathTools';
import { BasicButton } from '../../../lib/ui/BasicButton';
import { getApplication } from 'src/lib/utils';
import { GoalPickerPopup, GoalPickerPopupOptions } from './GoalPickerPopup';
import { BlockButton } from '../../ui/BlockButton';

// types
//-----------------------------------------------------------------------------
export type BlockPickerOptions = {
  goalIds: GoalId[];
  height: number;
};

// constants
//-----------------------------------------------------------------------------
const styles = {
  spacing: 0,
  arrow: 'assets/ui/match2/editor/buttons/button.arrow.png',
};

/*
    puzzle editor goal picker
*/
export class GoalPicker extends Container {
  // private
  //-------------------------------------------------------------------------
  // events
  public onUpdated?: BasicHandler;
  // input
  private readonly _goalIds: GoalId[];
  // scene
  private _goalIcon!: BlockButton;
  // state
  private _index!: number;

  // properties
  //-------------------------------------------------------------------------
  public get goalId(): GoalId {
    return this._goalIds[this._index];
  }

  public set goalId(goalId: GoalId) {
    this.index = this._goalIds.findIndex((id) => goalId === id);
  }

  public get index(): number {
    return this._index;
  }

  public set index(value: number) {
    // set index clamped
    this._index = numberClamp(value, 0, this._goalIds.length - 1);

    // notify
    this.onUpdated?.();

    // update icon
    this._updateIcon();
  }

  // init
  //-------------------------------------------------------------------------
  constructor(options: BlockPickerOptions) {
    super();

    this.width = 230;
    this.height = options.height;

    // set fields
    this._goalIds = options.goalIds;

    // spawn left button
    this._spawnLeftButton();

    // spawn goal icon
    this._spawnIcon();

    // spawn right button
    this._spawnRightButton();

    // set initial value
    this.index = 0;

    this.setBackgroundColor('rgba(255, 255, 255, 0.1)');
  }

  public static assets(): string[] {
    return [styles.arrow];
  }

  // private: updaters
  //-------------------------------------------------------------------------
  private _updateIcon() {
    this._goalIcon.def = { id: this._goalIds[this._index] };
  }

  // private: spawn
  //-------------------------------------------------------------------------
  private _spawnLeftButton() {
    const left = new BasicButton({
      up: styles.arrow,
      width: this.height * 0.8,
      height: this.height,
    });

    left.width = 53;
    left.height = 71;
    left.onTap = () => --this.index;
    left.initListeners();
    this.addChild(left);
    left.x = 0;
  }

  private _spawnRightButton() {
    const right = new BasicButton({
      up: styles.arrow,
      width: this.height * 0.8,
      height: this.height,
    });

    right.width = 53;
    right.height = 71;
    right.scaleX = -1;
    right.x = this.width;
    right.onTap = () => ++this.index;
    right.initListeners();
    this.addChild(right);
  }

  private _spawnIcon() {
    const icon = (this._goalIcon = new BlockButton({
      type: 'goal',
      def: { id: this.goalId },
    }));
    icon.anchorX = 0.5;
    icon.anchorY = 0.5;
    icon.width = this.height;
    icon.height = this.height;
    icon.scaleX = 0.6;
    icon.scaleY = 0.6;
    icon.x = this.width * 0.5 - 20;
    icon.y = this.height * 0.5 - 20;
    icon.interactive = true;
    icon.buttonMode = true;
    icon.setOnTap(() => {
      this.openGoalPickerPopup({
        selectedId: this.goalId,
        onOk: this._onPickerOk.bind(this),
      });
    });

    this.addChild(icon);
  }

  private async _onPickerOk(id: GoalId) {
    this.goalId = id;
  }

  private openGoalPickerPopup(opts: GoalPickerPopupOptions) {
    const superview = getApplication().getRootView();
    const goalPicker = new GoalPickerPopup();
    goalPicker.view.view.updateOpts({ superview });
    goalPicker.prepare(opts);
    goalPicker.show();
  }
}
